import React, { useState } from "react";
import { Table, Row, Col, Form, Button, Pagination } from "react-bootstrap";
import {
  BarChart3,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { CiSearch } from "react-icons/ci";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";
import { LuDownload } from "react-icons/lu";
import Chart from "../Charts/Chart";
import { FiChevronRight, FiChevronDown } from "react-icons/fi";
import { useTheme } from "../../Themecontext/Themecontext";

const GreyParentChild = ({ columns = [], data = [], parentKey, childKey }) => {

  const { Colourdata } = useSelector((state) => state.ClientLogin);


  const iscolour = Colourdata?.data?.find((el) => el.ExeName === "GRAYNEW");
  const iscolourDark = Colourdata?.data?.find(
    (el) => el.ExeName === "GRAYDARKNEW",
  );

  const { theme } = useTheme();
  const isdark = theme;

  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });


  // Jin columns ke Total mein "-" dikhana hai
  const excludeTotalColumns = ["LotNo", "ChallanNo", "ProductionGroupMstID", "DlvTp"];

  const noDecimalTotalColumns = ["InwTaka", "OpTaka", "Days", "DlvTaka", "BalTaka", "Gst"];

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const [selectedRow, setSelectedRow] = useState(null);

  const [expandedParent, setExpandedParent] = useState(new Set());
  const [expandedChild, setExpandedChild] = useState(new Set());

  const isSingleLevel = !childKey || childKey === parentKey;

  const toggleAllParents = () => {
    const allParents = Object.keys(groupedData);

    if (expandedParent.size === allParents.length) {
      setExpandedParent(new Set());
    } else {
      setExpandedParent(new Set(allParents));
    }
  };

  const toggleAllChildren = () => {
    const allChildren = [];

    if (!isSingleLevel) {
      Object.keys(groupedData).forEach((parent) => {
        Object.keys(groupedData[parent]).forEach((child) => {
          allChildren.push(parent + "-" + child);
        });
      });
    }

    if (expandedChild.size === allChildren.length) {
      setExpandedChild(new Set());
    } else {
      setExpandedChild(new Set(allChildren));
    }
  };

  /* SEARCH */
  const filteredData = data.filter((row) =>
    Object.values(row).some((val) =>
      val?.toString().toLowerCase().includes(search.trim().toLowerCase()),
    ),
  );

  /* NUMBER FORMAT */
  const formatIndianNumber = (num) => {
    const number = Number(num);
    if (isNaN(number)) return num;

    return number.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatRupees = (num) => {
    if (num === null || num === undefined || num === "") return "0";

    const number = parseFloat(num);

    if (isNaN(number)) return "0";

    return number.toLocaleString("en-IN"); // 👈 no decimal
  };

  const handleSort = (key) => {

    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });
  };

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;

    // ✅ FIX FOR SR NUMBER
    if (sortConfig.key === "srNumber") {
      return sortConfig.direction === "asc"
        ? filteredData.indexOf(a) - filteredData.indexOf(b)
        : filteredData.indexOf(b) - filteredData.indexOf(a);
    }

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    const cleanA = String(aValue).replace(/,/g, "");
    const cleanB = String(bValue).replace(/,/g, "");

    if (!isNaN(cleanA) && !isNaN(cleanB)) {
      aValue = Number(cleanA);
      bValue = Number(cleanB);
    }

    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;

    return 0;
  });

  // ---------parent child---------
  const groupData = () => {
    const grouped = {};

    //  SINGLE LEVEL (no child)
    if (isSingleLevel) {
      sortedData.forEach((row) => {
        const parent = row[parentKey];
        if (!grouped[parent]) grouped[parent] = [];
        grouped[parent].push(row);
      });
      return grouped;
    }

    //  TWO LEVEL (parent + child)
    sortedData.forEach((row) => {
      const parent = row[parentKey];
      const child = row[childKey];

      if (!grouped[parent]) grouped[parent] = {};

      if (!grouped[parent][child]) grouped[parent][child] = [];
      grouped[parent][child].push(row);
    });

    return grouped;
  };

  const groupedData = groupData();

  // ✅ 1. calculateTotals FIRST
  const calculateTotals = (rows) => {
    const totals = {};

    rows.forEach((row) => {
      Object.keys(row).forEach((key) => {
        const value = Number(String(row[key]).replace(/,/g, ""));
        if (!isNaN(value)) {
          totals[key] = (totals[key] || 0) + value;
        }
      });
    });

    return totals;
  };


  const sortedParents = Object.keys(groupedData).sort((a, b) => {
    const aRows = isSingleLevel
      ? groupedData[a]
      : Object.values(groupedData[a]).flat();

    const bRows = isSingleLevel
      ? groupedData[b]
      : Object.values(groupedData[b]).flat();

    const aTotal = calculateTotals(aRows)[sortConfig.key] || 0;
    const bTotal = calculateTotals(bRows)[sortConfig.key] || 0;

    if (sortConfig.direction === "asc") {
      return aTotal - bTotal;
    } else {
      return bTotal - aTotal;
    }
  });

  const toggleParent = (parent) => {
    const newSet = new Set(expandedParent);
    newSet.has(parent) ? newSet.delete(parent) : newSet.add(parent);
    setExpandedParent(newSet);
  };

  const toggleChild = (childKeyUnique) => {
    const newSet = new Set(expandedChild);
    newSet.has(childKeyUnique) ? newSet.delete(childKeyUnique) : newSet.add(childKeyUnique);
    setExpandedChild(newSet);
  };

  // const calculateTotals = (rows) => {
  //   const totals = {};

  //   rows.forEach((row) => {
  //     Object.keys(row).forEach((key) => {
  //       const value = Number(String(row[key]).replace(/,/g, ""));

  //       if (!isNaN(value)) {
  //         totals[key] = (totals[key] || 0) + value;
  //       }
  //     });
  //   });

  //   return totals;
  // };

  /* PAGINATION */
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  /* VISIBLE PAGE CONTROL */
  const visiblePages = 4;

  let startPage = Math.max(1, currentPage - Math.floor(visiblePages / 2));
  let endPage = startPage + visiblePages - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - visiblePages + 1);
  }

  // ------csv file-----
  const downloadCSV = () => {
    if (!sortedData || sortedData.length === 0) return;

    const headers = columns.map((col) => col.label);
    const rows = sortedData.map((row) =>
      columns.map((col) => {
        let value = row[col.key];
        if (typeof value === "string" && value.includes(",")) {
          value = `"${value}"`;
        }
        return value ?? "";
      }),
    );

    const csvContent = [headers, ...rows].map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "table_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderSingleLevel = () => {
    return (
      <tbody>
        {sortedParents.map((parent, pIndex) => {
          const isParentOpen = expandedParent.has(parent);
          const parentRows = groupedData[parent];
          const totals = calculateTotals(parentRows);

          return (
            <React.Fragment key={pIndex}>
              {/* PARENT ROW */}
              <tr
                onClick={() => toggleParent(parent)}
                style={{
                  backgroundColor: isdark === "light" ? iscolour?.Colour5 : iscolourDark?.Colour7,
                  color: isdark === "light" ? iscolour?.Colour2 : iscolourDark?.Colour2,
                  cursor: "pointer",
                  fontWeight: "700",
                }}
              >
                <td style={{ textAlign: "center", padding: "0px 10px" }}>
                  <div
                    style={{
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      background: isParentOpen ? "#4CAF7A" : "#6BBF8E",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      margin: "auto",
                    }}
                  >
                    {isParentOpen ? <FiChevronDown /> : <FiChevronRight />}
                  </div>
                </td>


                {columns.map((col, i) => {
                  let value = "";

                  if (col.key === parentKey) {
                    value = parent;
                  } else if (col.key === "srNumber") {
                    value = parentRows.length;
                  } else if (totals[col.key] !== undefined) {
                    value = totals[col.key];
                  } else {
                    value = "(Multiple)";
                  }

                  return (
                    <td key={i} style={{ padding: "15px", textAlign: col.align || "left" }}>
                      {col.rupees ? (
                        formatRupees(value)

                      ) : col.format ? (
                        formatIndianNumber(value)
                      ) : (value)}
                    </td>
                  );
                })}
              </tr>

              {/* CHILD ROWS (actual data) */}
              {isParentOpen && parentRows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  style={{
                    backgroundColor: rowIndex % 2 === 0
                      ? (isdark === "light" ? iscolour?.Colour5 : iscolourDark?.Colour7)
                      : (isdark === "light" ? iscolour?.Colour7 : iscolourDark?.Colour6),
                  }}
                >
                  <td style={{ width: "40px" }}></td>
                  {columns.map((col, colIndex) => {
                    let value = row[col.key];

                    if (col.key === "srNumber") {
                      value = rowIndex + 1;
                    } else if (rowIndex > 0 && (col.key === parentKey)) {
                      value = "";
                    }

                    return (
                      <td
                        key={colIndex}
                        style={{
                          padding: "15px",
                          textAlign: col.align || "left",
                          fontSize: "14px",
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                          color: col.colors || (isdark === "light" ? "#000" : iscolourDark?.Colour12),
                        }}
                      >
                        {col.key === "charts" ? (
                          <div
                            onClick={() => setSelectedRow(row)}
                            style={{
                              background: "#cde3d5",
                              width: "32px",
                              height: "32px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "8px",
                              color: "#2d7a57",
                              margin: "auto",
                              cursor: "pointer",
                            }}
                          >
                            <BarChart3 size={16} />
                          </div>
                        ) : col.rupees ? (
                          formatRupees(value)

                        ) : col.format ? (
                          formatIndianNumber(value)
                        ) : (
                          value
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}

              {/* PARENT TOTAL ROW */}
              {isParentOpen && (
                <tr style={{
                  background: isdark === "light" ? iscolour?.Colour7 : iscolourDark?.Colour12,
                  color: isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour5,
                  fontWeight: "bold"
                }}>
                  <td></td>
                  {columns.map((col, i) => {
                    let value = "";

                    if (col.key === parentKey) {
                      value = "Total";
                    } else if (
                      col.key === "srNumber" ||
                      excludeTotalColumns.includes(col.key)
                    ) {
                      value = "-";
                    } else if (totals[col.key] !== undefined) {
                      value = noDecimalTotalColumns.includes(col.key)
                        ? formatRupees(totals[col.key])
                        : formatIndianNumber(totals[col.key]);
                    }

                    return (
                      <td key={i} style={{ padding: "14px", textAlign: col.align || "left" }}>
                        {value || "-"}
                      </td>
                    );
                  })}
                </tr>
              )
              }

            </React.Fragment>

          );
        })
        }
      </tbody >
    );
  };

  const renderTwoLevel = () => {
    return (
      <tbody>
        {sortedParents.map((parent, pIndex) => {
          const isParentOpen = expandedParent.has(parent);

          return (
            <React.Fragment key={pIndex}>
              {/* PARENT ROW */}
              <tr
                onClick={() => toggleParent(parent)}
                style={{
                  backgroundColor: isdark === "light" ? iscolour?.Colour5 : iscolourDark?.Colour7,
                  color: isdark === "light" ? iscolour?.Colour2 : iscolourDark?.Colour2,
                  cursor: "pointer",
                  fontWeight: "700",
                }}
              >
                <td style={{ textAlign: "center", padding: "0px 10px" }}>
                  <div
                    style={{
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      background: isParentOpen ? "#4CAF7A" : "#6BBF8E",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      margin: "auto",
                    }}
                  >
                    {isParentOpen ? <FiChevronDown /> : <FiChevronRight />}
                  </div>
                </td>

                {columns.map((col, i) => {
                  const parentRows = Object.values(groupedData[parent]).flat();
                  const totals = calculateTotals(parentRows);
                  let value = "";

                  if (col.key === parentKey) {
                    value = parent;
                  } else if (col.key === "srNumber") {
                    value = parentRows.length;
                  } else if (totals[col.key] !== undefined) {
                    value = totals[col.key];
                  } else {
                    value = "(Multiple)";
                  }

                  return (
                    <td key={i} style={{ padding: "15px", textAlign: col.align || "left" }}>
                      {col.rupees ? (
                        formatRupees(value)

                      ) : col.format ? (
                        formatIndianNumber(value)
                      ) : (value)}
                    </td>
                  );
                })}
              </tr>

              {/* CHILD LEVEL */}
              {isParentOpen && Object.keys(groupedData[parent]).map((child, cIndex) => {
                const childKeyUnique = parent + "-" + child;
                const isChildOpen = expandedChild.has(childKeyUnique);
                const childRows = groupedData[parent][child];
                const childTotals = calculateTotals(childRows);

                return (
                  <React.Fragment key={cIndex}>
                    {/* CHILD ROW */}
                    <tr
                      onClick={() => toggleChild(childKeyUnique)}
                      style={{
                        backgroundColor: isdark === "light" ? "rgb(249, 250, 251)" : iscolourDark?.Colour7,
                        color: isdark === "light" ? iscolour?.Colour2 : iscolourDark?.Colour2,
                        cursor: "pointer",
                      }}
                    >
                      <td style={{ textAlign: "center" }}>
                        <div
                          style={{
                            width: "25px",
                            height: "25px",
                            borderRadius: "50%",
                            background: isChildOpen ? "#4CAF7A" : "#9FD3B5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            margin: "auto",
                          }}
                        >
                          {isChildOpen ? <FiChevronDown /> : <FiChevronRight />}
                        </div>
                      </td>

                      {columns.map((col, i) => {
                        let value = "";

                        if (col.key === childKey) {
                          value = child;
                        } else if (col.key === "srNumber") {
                          value = childRows.length;
                        } else if (childTotals[col.key] !== undefined) {
                          value = childTotals[col.key];
                        } else {
                          value = "(Multiple)";
                        }

                        return (
                          <td key={i} style={{ padding: "15px", textAlign: col.align || "left" }}>
                            {col.rupees ? (
                              formatRupees(value)

                            ) : col.format ? (
                              formatIndianNumber(value)
                            ) : (value)}
                          </td>
                        );
                      })}
                    </tr>

                    {/* ACTUAL DATA ROWS */}
                    {isChildOpen && childRows.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        style={{
                          backgroundColor: rowIndex % 2 === 0
                            ? (isdark === "light" ? iscolour?.Colour5 : iscolourDark?.Colour7)
                            : (isdark === "light" ? iscolour?.Colour7 : iscolourDark?.Colour6),
                        }}
                      >
                        <td style={{ width: "40px" }}></td>
                        {columns.map((col, colIndex) => {
                          let value = row[col.key];

                          if (col.key === "srNumber") {
                            value = rowIndex + 1;
                          } else if (rowIndex > 0 && (col.key === parentKey || col.key === childKey)) {
                            value = "";
                          }

                          return (
                            <td
                              key={colIndex}
                              style={{
                                padding: "15px",
                                textAlign: col.align || "left",
                                fontSize: "14px",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                                color: col.colors || (isdark === "light" ? "#000" : iscolourDark?.Colour12),
                              }}
                            >
                              {col.key === "charts" ? (
                                <div
                                  onClick={() => setSelectedRow(row)}
                                  style={{
                                    background: "#cde3d5",
                                    width: "32px",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "8px",
                                    color: "#2d7a57",
                                    margin: "auto",
                                    cursor: "pointer",
                                  }}
                                >
                                  <BarChart3 size={16} />
                                </div>
                              ) : col.rupees ? (
                                formatRupees(value)

                              ) : col.format ? (
                                formatIndianNumber(value)
                              ) : (
                                value
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}

                    {/* CHILD TOTAL ROW */}
                    {isChildOpen && (
                      <tr style={{
                        background: isdark === "light" ? iscolour?.Colour7 : iscolourDark?.Colour12,
                        color: isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour5,
                        fontWeight: "bold"
                      }}>
                        <td></td>
                        {columns.map((col, i) => {
                          let value = "";

                          if (col.key === childKey) {
                            value = "Total";
                          } else if (
                            col.key === "srNumber" ||
                            excludeTotalColumns.includes(col.key)
                          ) {
                            value = "-";
                          } else if (childTotals[col.key] !== undefined) {
                            value = noDecimalTotalColumns.includes(col.key)
                              ? formatRupees(childTotals[col.key])
                              : formatIndianNumber(childTotals[col.key]);
                          }

                          return (
                            <td key={i} style={{ padding: "12px", textAlign: col.align || "left" }}>
                              {value || "-"}
                            </td>
                          );
                        })}
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {/* PARENT TOTAL ROW */}
              {isParentOpen && (() => {
                const parentRows = Object.values(groupedData[parent]).flat();
                const totals = calculateTotals(parentRows);

                return (
                  <tr style={{
                    background: isdark === "light" ? iscolour?.Colour7 : iscolourDark?.Colour12,
                    color: isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour5, fontWeight: "bold"
                  }}>
                    <td></td>
                    {columns.map((col, i) => {
                      let value = "";

                      if (col.key === parentKey) {
                        value = "Total";
                      } else if (
                        col.key === "srNumber" ||
                        excludeTotalColumns.includes(col.key)
                      ) {
                        value = "-";
                      } else if (totals[col.key] !== undefined) {
                        value = noDecimalTotalColumns.includes(col.key)
                          ? formatRupees(totals[col.key])
                          : formatIndianNumber(totals[col.key]);
                      }

                      return (
                        <td key={i} style={{ padding: "14px", textAlign: col.align || "left" }}>
                          {value || "-"}
                        </td>
                      );
                    })}
                  </tr>
                );
              })()}
            </React.Fragment>
          );
        })}
      </tbody>
    );
  };

  return (
    <>
      <style>{`
        .custom-pagination {
          gap: 8px;
        }
        .custom-pagination .page-item .page-link {
          border: none;
          border-radius: 8px;
          min-width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${isdark === "light" ? iscolour?.Colour7 : iscolourDark?.Colour6};
          color: ${isdark === "light" ? iscolour?.Colour2 : iscolourDark?.Colour2};
          font-weight: 500;
        }
        .custom-pagination .page-item.active .page-link {
          background: ${isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour1};
          color: ${isdark === "light" ? iscolour?.Colour5 : iscolourDark?.Colour8};
        }
        .custom-pagination .page-item .page-link:hover {
          background: ${isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour1};
          color: ${isdark === "light" ? iscolour?.Colour5 : iscolourDark?.Colour8};
        }
        .search-input::placeholder {
          color: ${isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour1};
          opacity: 1;
        }


.search-input:focus{
  border-color: ${isdark === "light"
          ? iscolour?.Colour1
          : iscolourDark?.Colour1} !important;

  box-shadow: 0 0 0 0.2rem ${isdark === "light"
          ? `${iscolour?.Colour1}40`
          : `${iscolourDark?.Colour1}40`
        } !important;

  outline: none !important;
}
        @media (max-width: 991px) {
          .search-input {
            font-size: 13px !important;
          }
        }
        @media (max-width: 768px) {
          .search-wrapper {
            flex: 1 !important;
            width: auto !important;
            min-width: 0 !important;
          }
          .download-btn {
            width: 40px;
            height: 40px;
            padding: 8px;
          }
          .download-text {
            display: none;
          }
        }
        .hover-scale-btn {
          transition: transform 0.2s ease-in-out;
        }
        .hover-scale-btn:hover {
          transform: scale(1.02);
        }
        @media (max-width: 576px) {
          .top-bar-wrap {
            gap: 8px !important;
          }
        }
      `}</style>

      <div
        style={{
          width: "100%",
          background: isdark === "light" ? iscolour?.Colour7 : iscolourDark?.Colour6,
        }}
      >
        {/* TOP BAR */}
        <Row className="mb-2 g-2 align-items-center m-0 px-2 px-sm-2 px-md-3">
          <Col xs={12} md={5} lg={5} xl={4}>
            <div style={{ position: "relative" }}>
              <CiSearch
                size={20}
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: isdark === "light" ? iscolour?.Colour6 : iscolourDark?.Colour5,
                  zIndex: 1,
                }}
              />
              <Form.Control
                type="text"
                className="search-input"
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  borderRadius: "8px",
                  height: "40px",
                  padding: "0 10px 0 35px",
                  border: `1px solid ${isdark === "light" ? iscolour?.Colour4 : iscolourDark?.Colour3}`,
                  backgroundColor: isdark === "light" ? iscolour?.Colour5 : iscolourDark?.Colour7,
                  color: isdark === "light" ? iscolour?.Colour2 : iscolourDark?.Colour2,
                }}
              />
            </div>
          </Col>

          <Col xs={12} md={7} lg={7} xl={8}>
            <div
              style={{
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
              }}
              className="justify-content-center justify-content-md-center justify-content-xl-end"
            >
              <Button
                onClick={toggleAllParents}
                style={{
                  background: isdark === "light" ? iscolour?.Colour12 : iscolourDark?.Colour11,
                  color: isdark === "light" ? iscolour?.Colour2 : iscolourDark?.Colour1,
                  border: `1px solid ${isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour1}`,
                  borderRadius: "8px",
                  height: "40px",
                  fontSize: "13px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",

                }}
              >
                {expandedParent.size === Object.keys(groupedData).length
                  ? "Close All"
                  : "Open All"}
              </Button>

              {!isSingleLevel && (
                <Button
                  onClick={toggleAllChildren}
                  style={{
                    background: isdark === "light" ? iscolour?.Colour12 : iscolourDark?.Colour11,
                    color: isdark === "light" ? iscolour?.Colour2 : iscolourDark?.Colour1,
                    border: `1px solid ${isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour1}`,
                    borderRadius: "8px",
                    height: "40px",
                    fontSize: "13px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  {expandedChild.size > 0 ? "Close Child" : "Open Child"}
                </Button>
              )}

              <Button
                onClick={downloadCSV}
                className="hover-scale-btn"
                style={{
                  background: isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour1,
                  color: isdark === "light" ? iscolour?.Colour5 : iscolourDark?.Colour8,
                  border: "none",
                  borderRadius: "8px",
                  height: "40px",
                  fontSize: "14px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <LuDownload size={16} />
                Download CSV
              </Button>
            </div>
          </Col>
        </Row>

        <div
          style={{
            position: "relative",
            minHeight: "400px", // 👈 important
          }}
        >

          {/* ================= EMPTY STATE ================= */}

          {currentRows.length === 0 && (

            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  isdark === "light"
                    ? iscolour?.Colour5
                    : iscolourDark?.Colour7,
                overflow: "hidden",
                // borderRadius: "10px",
              }}
            >

              {/* BACKGROUND CIRCLES */}
              <div
                style={{
                  position: "absolute",
                  width: "250px",
                  height: "250px",
                  borderRadius: "50%",
                  background:
                    isdark === "light"
                      ? "rgba(0,0,0,0.03)"
                      : "rgba(255,255,255,0.03)",
                  top: "-80px",
                  right: "-80px",
                }}
              />

              <div
                style={{
                  position: "absolute",
                  width: "180px",
                  height: "180px",
                  borderRadius: "50%",
                  background:
                    isdark === "light"
                      ? "rgba(0,0,0,0.03)"
                      : "rgba(255,255,255,0.03)",
                  bottom: "-60px",
                  left: "-60px",
                }}
              />

              {/* PREMIUM EMPTY STATE CARD */}
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  maxWidth: "450px",
                  padding: "40px",
                  borderRadius: "24px",
                  background: isdark === "light"
                    ? "rgba(255, 255, 255, 0.6)"
                    : "rgba(30, 30, 35, 0.6)",
                  boxShadow: isdark === "light"
                    ? "0 8px 32px rgba(0, 0, 0, 0.05)"
                    : "0 8px 32px rgba(0, 0, 0, 0.3)",
                  backdropFilter: "blur(12px)",
                  border: isdark === "light"
                    ? "1px solid rgba(255,255,255,0.8)"
                    : "1px solid rgba(255,255,255,0.05)",
                  textAlign: "center",
                  margin: "20px auto",
                }}
              >
                {/* ICON CONTAINER WITH GLOW */}
                <div
                  style={{
                    width: "90px",
                    height: "90px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "24px",
                    background: isdark === "light"
                      ? `${iscolour?.Colour11 || "#1e293b"}1A`
                      : `${iscolourDark?.Colour12 || "#f1f5f9"}26`,
                    boxShadow: isdark === "light"
                      ? `0 10px 25px -5px ${iscolour?.Colour11 || "#1e293b"}33`
                      : `0 10px 25px -5px ${iscolourDark?.Colour12 || "#f1f5f9"}4D`,
                    color: isdark === "light" ? "#1e293b" : "#f1f5f9",
                  }}
                >
                  {search ? (
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.3-4.3" />
                      <path d="m9 9 4 4" />
                      <path d="m13 9-4 4" />
                    </svg>
                  ) : (
                    <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
                      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
                    </svg>
                  )}
                </div>

                <h2
                  style={{
                    margin: "0 0 12px 0",
                    fontWeight: "700",
                    fontSize: "22px",
                    color: isdark === "light" ? "#1e293b" : "#f1f5f9",
                    letterSpacing: "-0.5px"
                  }}
                >
                  {search ? (
                    <span>
                      No results for <span style={{ color: isdark === "light" ? "#1e293b" : "#f1f5f9" }}>"{search}"</span>
                    </span>
                  ) : (
                    "No Data Available"
                  )}
                </h2>

                <p
                  style={{
                    margin: 0,
                    fontSize: "15px",
                    lineHeight: "1.6",
                    color: isdark === "light" ? "#64748b" : "#94a3b8",
                  }}
                >
                  {search
                    ? "We couldn't find anything matching your search. Please try a different term or clear the filter."
                    : "There are currently no records to display."
                  }
                </p>
              </div>

            </div>
          )}
          {/* TABLE */}
          <div
            className="grey-scroll"
            style={{
              overflowX: "auto",
              overflowY: "auto",
              width: "100%",
              height: "400px",
              scrollbarWidth: "thin",
              scrollbarColor: isdark === "light"
                ? `${iscolour?.Colour1} ${iscolour?.Colour4}`
                : `${iscolourDark?.Colour3} ${iscolourDark?.Colour7}`,
            }}
          >
            <table
              style={{
                borderCollapse: "collapse",
                width: "100%",
                minWidth: "900px",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      width: "40px",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      background: isdark === "light" ? iscolour?.Colour7 : iscolourDark?.Colour6,
                    }}
                  ></th>
                  {columns.map((col, index) => (
                    <th
                      key={index}
                      onClick={() => handleSort(col.key)}
                      style={{
                        padding: "15px",
                        fontWeight: "700",
                        fontSize: "13px",
                        textAlign: "center",
                        cursor: "pointer",
                        verticalAlign: "middle",
                        whiteSpace: "nowrap",
                        position: "sticky",
                        top: 0,
                        background: isdark === "light" ? iscolour?.Colour7 : iscolourDark?.Colour6,
                        color: isdark === "light" ? iscolour?.Colour2 : iscolourDark?.Colour2,
                        zIndex: 1,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                        }}
                      >
                        {col.label}
                        {sortConfig.key === col.key ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUp size={14} />
                          ) : (
                            <ArrowDown size={14} />
                          )
                        ) : (
                          <ArrowUpDown size={14} />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>


              {isSingleLevel ? renderSingleLevel() : renderTwoLevel()}


            </table>
          </div>
        </div>

        {/* FOOTER */}
        {currentRows.length > 0 && (
          <div style={{ margin: "0px 10px" }}>
            <Row
              style={{
                backgroundColor: isdark === "light" ? iscolour?.Colour5 : iscolourDark?.Colour7,
                padding: "10px",
              }}
            >
              <Col
                sm={12}
                md={6}
                lg={6}
                className="text-center text-md-start p-0 pb-md-2"
                style={{
                  fontSize: "13px",
                  color: isdark === "light" ? "#000" : (iscolourDark?.Colour1 || "#fff")
                }}
              >
                <div>
                  Showing {indexOfFirstRow + 1} to{" "}
                  {Math.min(indexOfLastRow, sortedData.length)} of{" "}
                  {sortedData.length} results
                </div>
              </Col>

              <Col
                sm={12}
                md={6}
                lg={6}
                className="d-flex justify-content-center p-0 justify-content-md-end"
              >
                <Pagination className="custom-pagination">
                  <Pagination.Item
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <ChevronLeft size={14} />
                  </Pagination.Item>

                  {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                    const page = startPage + i;
                    return (
                      <Pagination.Item
                        key={page}
                        active={currentPage === page}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  })}

                  <Pagination.Item
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <ChevronRight size={14} />
                  </Pagination.Item>
                </Pagination>
              </Col>
            </Row>
          </div>
        )}
      </div>

      {selectedRow && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setSelectedRow(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "90%",
              maxWidth: "1100px",
              background:
                isdark === "light" ? iscolour?.Colour7 : iscolourDark?.Colour7 || "#fff",
              borderRadius: "12px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
            }}
            className="p-2 p-sm-3"
          >
            <Row>
              <Col xs={12} className="d-flex justify-content-end mb-3">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setSelectedRow(null)}
                >
                  Close
                </Button>
              </Col>
              <Col xs={12} className="p-0">
                <Chart noWrapper={true} data={selectedRow} />
              </Col>
            </Row>
          </div>
        </div>
      )}
    </>
  );
};

export default GreyParentChild;